import { useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0, IdToken } from '@auth0/auth0-react';
import auth0 from 'auth0-js';
import { WAS_BACK_END_API_URL, URL_VERSION } from 'src/constants';
import { fetchAuthenticated } from 'src/controllers';
import { ApproverAjaxResult, ApproverResult, UserSession, WebAuthConfig } from 'src/shared/types/types';
import { GlobalUserContext } from '../../shared/contexts/GlobalUserContext';
import { env } from 'src/env';
import { UserAuthenticatedContext } from 'src/shared/contexts/UserAuthenticatedContext';

export interface UseAuthentication {
  logOut: () => void;
  login: (result: IdToken) => void;
  webAuthConfig: WebAuthConfig;
  webAuth: auth0.WebAuth;
  getDbUserObjectId: () => string;
  getTokenSilently: () => any;
}

export function useAuthentication(): UseAuthentication {
  const [authState, setAuthState] = useState<IdToken | null>(null);
  const [token, setToken] = useState<IdToken | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [userDocId, setUserDocId] = useState<string>('');

  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const [dbUser, setDbUser] = useState<ApproverResult | undefined>();

  const globalUserState = useContext(GlobalUserContext);
  const { setUserAuthenticated } = useContext(UserAuthenticatedContext);

  const webAuthConfig: WebAuthConfig = {
    clientID: `${env.REACT_APP_AUTH0_CLIENT_ID}`,
    domain: `${env.REACT_APP_AUTH0_DOMAIN}`,
    audience: `https://${env.REACT_APP_AUTH0_DOMAIN}/api/v2/`,
    redirectUri: `${env.REACT_APP_REDIRECT_URI}`,
    responseType: 'token id_token',
    returnTo: `${window.origin}/handlelogin`,
  };

  const webAuth = new auth0.WebAuth({
    domain: webAuthConfig.domain,
    clientID: webAuthConfig.clientID,
    responseType: webAuthConfig.responseType,
    redirectUri: webAuthConfig.redirectUri,
  });

  const systemId = navigator.userAgent;

  const getTokenSilently = useCallback(async () => {
    if (dbUser?.userSession?._id && dbUser?.userSession?.loginInfo?.__raw) {
      try {
        const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/authenticateapproververify/${dbUser?.userSession?._id}`;
        const config: RequestInit = {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${dbUser?.userSession?.loginInfo?.__raw}`,
          },
        };
        const response = await fetchAuthenticated<ApproverAjaxResult>(url, config);
        return response.parsedBody?.result?.userSession?.loginInfo;
      } catch (error) {
        return null;
      }
    } else {
      return await getIdTokenClaims();
    }
  }, [dbUser, getIdTokenClaims]);

  const getIDtoken = useCallback(async () => {
    const tokenClaim = await getTokenSilently();
    localStorage.setItem('token', JSON.stringify(tokenClaim));
    tokenClaim && setAuthState(tokenClaim);
    tokenClaim && setUserAuthenticated(tokenClaim);
  }, [getTokenSilently, setUserAuthenticated]);

  const handleUserLoginInformation = useCallback(
    (existingSession: UserSession | any) => {
      const { _id, loginInfo } = existingSession;
      globalUserState.setUserId?.(_id);
      globalUserState.setFirstName?.(loginInfo.name?.split(' ')[0]);
      globalUserState.setLastName?.(loginInfo.name?.split(' ')[1]);
      globalUserState.setUserName?.(loginInfo?.upn);
      globalUserState.setUserInfo(loginInfo?.groups);
    },
    [globalUserState]
  );

  const getSilentToken = useCallback(async () => {
    let tokenClaim;
    try {
      if (localStorage?.token) {
        tokenClaim = JSON.parse(localStorage.token);
        const isTokenExpired = new Date().getTime() > tokenClaim.expiry;
        if (isTokenExpired) {
          tokenClaim = await getIdTokenClaims();
          if (tokenClaim) {
            localStorage.setItem('token', JSON.stringify(tokenClaim)); // Save new token
            tokenClaim && setToken(tokenClaim);
            // Update the backend with the new info
            const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/getsession`;
            const body = JSON.stringify({
              ...tokenClaim,
              systemId,
            });
            const config: RequestInit = {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${tokenClaim.__raw}`,
              },
              body,
            };
            const response: any = await fetchAuthenticated<ApproverAjaxResult>(url, config);
            setDbUser(response.parsedBody?.result);
            setUserDocId(response?.parsedBody?.result?.userSession?._id);
            handleUserLoginInformation(response?.parsedBody?.result?.userSession);
          }
        }
      } else {
        tokenClaim = await getTokenSilently();
        tokenClaim && setToken(tokenClaim);
      }
    } catch (error) {
      // TODO: Error handling for not authenticated from SAML
    }
  }, [getIdTokenClaims, getTokenSilently, systemId, handleUserLoginInformation]);

  const setAuthContext = useCallback(async () => {
    if (authState && authState?.isAuthenicated) {
      globalUserState?.setIsLoggedIn?.(true);
      const [firstName, lastName] = authState?.name?.split(' ') || [];
      globalUserState?.setFirstName?.(firstName);
      globalUserState?.setLastName?.(lastName);
      globalUserState?.setUserName?.(authState?.email || '');
      globalUserState?.setToken?.(authState?.__raw || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, authState?.isAuthenticated]);

  useEffect(() => {
    !isLoggedIn && isAuthenticated && authState && !isLoggingIn && login(authState);
    // eslint-disable-next-line
  }, [authState]); // Beware of multiple approver documents being created if you update this

  useEffect(() => {
    isLoggedIn && authState && verifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, authState]);

  useEffect(() => {
    getSilentToken();
    getIDtoken();
    setAuthContext();
  }, [getIDtoken, getIdTokenClaims, getSilentToken, isAuthenticated, setAuthContext, token]);

  const login = async (result: IdToken) => {
    if (window?.location?.search === '?nologin') return;
    if (!authState || isLoggedIn || isLoggingIn) return;
    setIsLoggingIn(true);
    const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/loginfirstuser`;
    const body = JSON.stringify({
      ...result,
      systemId,
    });
    const config: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${result.__raw}`,
      },
      body,
    };
    const response: any = await fetchAuthenticated<ApproverAjaxResult>(url, config);
    setDbUser(response.parsedBody?.result);

    setUserDocId(response?.parsedBody?.result?.userSession?._id);
    handleUserLoginInformation(response?.parsedBody?.result?.userSession);
    setIsLoggedIn(true);
    setIsLoggingIn(false);

    return [response.parsedBody?.result];
  };

  // Function to clear a specific cookie
  function clearCookie(name: string, domain: string) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + domain + ';';
  }

  const logOutUser = async () => {
    globalUserState?.setIsLoggedIn?.(false);
    setUserAuthenticated(null);
    globalUserState?.setFirstName?.('');
    globalUserState.setLastName?.('');
    globalUserState.setUserName?.('');
    globalUserState.setUserId?.('');
    setUserDocId('');
    setIsLoggedIn(false);

    if (dbUser && dbUser?.userSession) {
      const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/authuserdelete/${dbUser?.userSession?._id}`;
      const config: RequestInit = {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authState?.__raw}`,
        },
      };
      await fetchAuthenticated<ApproverAjaxResult>(url, config);
      // Prepare logout options for custom logout functionality
      // Clear cookies or local storage as needed
      clearCookie('orgHint', window.origin);
      clearCookie('isAuthenticated', window.origin);

      const logoutUrl = `https://${env.REACT_APP_AUTH0_DOMAIN}/v2/logout?returnTo=${encodeURIComponent(window.location.origin)}&client_id=${
        env.REACT_APP_AUTH0_CLIENT_ID
      }&auth0Client=${authState?.loginInfo?.__raw}`;
      window.location.assign(logoutUrl);
    }
  };

  const verifyUser = async () => {
    try {
      if (!dbUser || !dbUser?.userSession) return;
      const tokenIDClaim = await getIdTokenClaims();
      const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/authenticateapproververify/${dbUser?.userSession?._id}`;
      const config: RequestInit = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokenIDClaim?.__raw}`,
        },
      };
      const response = await fetchAuthenticated<ApproverAjaxResult>(url, config);
      if (response?.ok && response?.parsedBody) {
        setDbUser(response.parsedBody?.result);
      }
    } catch (error) {}
  };

  const getDbUserObjectId = () => {
    return userDocId;
  };

  return {
    logOut: logOutUser,
    login,
    webAuthConfig,
    webAuth,
    getDbUserObjectId,
    getTokenSilently,
  };
}
